import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChefHat, Phone, ArrowRight, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const ProductSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get state from navigation (passed from OAuthCallback)
  const hasExtractedMenu = location.state?.hasExtractedMenu;
  const itemsExtracted = location.state?.itemsExtracted;
  const fromOAuth = location.state?.fromOAuth;
  const prefillData = location.state?.prefillData;
  const isOnboarding = location.state?.onboarding;
  const isActivation = location.state?.isActivation;  // From activation flow
  const preSelectedProduct = location.state?.preSelectedProduct;  // Pre-selected by sales rep

  // Pre-select product if passed from activation flow, otherwise empty
  const [selectedProduct, setSelectedProduct] = useState<string>(preSelectedProduct || '');

  const handleProductSelect = (productType: 'virtual_assistant' | 'virtual_host') => {
    // Store product type selection
    localStorage.setItem('selected_product_type', productType);

    // Navigate to plan selection with preserved state
    navigate('/plan-selection', {
      state: {
        hasExtractedMenu,
        itemsExtracted,
        fromOAuth,
        prefillData,
        onboarding: isOnboarding,
        productType,
        isActivation  // Preserve activation flag for plan selection
      }
    });
  };

  const products = [
    {
      type: 'virtual_host' as const,
      name: 'Virtual Host',
      tagline: 'AI Phone Assistant 24/7',
      description: 'Never miss a call or order again. AI-powered phone assistant handles orders, payments, and customer questions around the clock.',
      icon: Phone,
      gradient: 'from-pink-500 to-purple-500',
      features: [
        'AI phone ordering 24/7',
        'Automated payment processing',
        'SMS order notifications',
        'Real-time order queue',
        'Call analytics dashboard',
        'Emotional AI voice technology'
      ],
      popular: true
    },
    {
      type: 'virtual_assistant' as const,
      name: 'Virtual Assistant',
      tagline: 'AI-Powered Menu Intelligence',
      description: 'Transform your menu into a smart digital experience with AI-generated images, insights, and personalized customer recommendations.',
      icon: ChefHat,
      gradient: 'from-blue-500 to-purple-500',
      features: [
        'AI-generated menu item images',
        'Smart menu analytics & insights',
        'Customer recommendations engine',
        'Digital menu optimization',
        'Multi-location support',
        'Real-time menu updates'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            {hasExtractedMenu && (
              <div className="inline-flex items-center gap-2 bg-dineai-yellow/10 border border-dineai-yellow/20 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-dineai-yellow" />
                <span className="text-dineai-yellow text-sm font-semibold">
                  {itemsExtracted} items extracted from your menu!
                </span>
              </div>
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Select Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-dineai-yellow to-yellow-300">AI Solution</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Both products work seamlessly with your menu. Choose the solution that fits your restaurant's needs.
            </p>
          </motion.div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {products.map((product, index) => (
              <motion.div
                key={product.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white/10 backdrop-blur-sm rounded-2xl border transition-all duration-300 cursor-pointer ${
                  selectedProduct === product.type
                    ? 'border-dineai-yellow shadow-lg shadow-dineai-yellow/20'
                    : product.popular
                    ? 'border-dineai-yellow/40 hover:border-dineai-yellow/60'
                    : 'border-white/20 hover:border-white/30'
                }`}
                onClick={() => setSelectedProduct(product.type)}
              >
                {/* Popular Badge */}
                {product.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-dineai-yellow text-blue-900 px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Most Popular
                    </div>
                  </div>
                )}

                {/* Selected Indicator */}
                {selectedProduct === product.type && (
                  <div className="absolute top-4 left-4 bg-dineai-yellow rounded-full p-1">
                    <Check className="w-4 h-4 text-blue-900" />
                  </div>
                )}

                <div className={`p-6 ${product.popular ? 'pt-8' : 'pt-6'}`}>
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${product.gradient} flex items-center justify-center mb-4`}>
                    <product.icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-1">
                    {product.name}
                  </h3>
                  <p className="text-dineai-yellow text-sm font-semibold mb-3">
                    {product.tagline}
                  </p>

                  {/* Description */}
                  <p className="text-white/70 text-sm mb-5 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2.5 mb-5">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className={`mt-0.5 w-4 h-4 rounded-full bg-gradient-to-br ${product.gradient} flex items-center justify-center flex-shrink-0`}>
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                        <span className="text-white/80 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Select Button */}
                  <Button
                    onClick={() => handleProductSelect(product.type)}
                    className={`w-full py-3 text-base font-semibold rounded-lg transition-all ${
                      selectedProduct === product.type
                        ? 'bg-dineai-yellow text-blue-900 hover:bg-yellow-300'
                        : product.popular
                        ? 'bg-dineai-yellow text-blue-900 hover:bg-yellow-300'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    Select {product.name}
                    <ArrowRight className="w-4 h-4 ml-2 inline" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center"
          >
            <p className="text-white/50 text-sm">
              Not sure which to choose? You can always add more products later from your dashboard.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductSelection;
